<template>
    <div v-if="isOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <!-- Header -->
            <div class="flex items-center justify-between p-6 border-b border-gray-200">
                <div class="flex items-center space-x-3">
                    <div class="p-2 bg-blue-100 rounded-lg">
                        <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10">
                            </path>
                        </svg>
                    </div>
                    <div>
                        <h2 class="text-xl font-semibold text-gray-900">Import de Sections</h2>
                        <p class="text-sm text-gray-500">Étape {{ step }} sur 4</p>
                    </div>
                </div>
                <button class="text-gray-400 hover:text-gray-600 transition-colors" :disabled="importing"
                    @click="closeModal">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12">
                        </path>
                    </svg>
                </button>
            </div>

            <!-- Progress Bar -->
            <div class="px-6 py-2 bg-gray-50">
                <div class="flex items-center space-x-4">
                    <div class="flex-1 bg-gray-200 rounded-full h-2">
                        <div class="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            :style="{ width: `${(step / 4) * 100}%` }"></div>
                    </div>
                    <span class="text-sm font-medium text-gray-600">{{ Math.round((step / 4) * 100) }}%</span>
                </div>
            </div>

            <!-- Content -->
            <div class="p-6 overflow-y-auto max-h-[60vh]">
                <!-- Étape 1: Sélection du fichier -->
                <div v-if="step === 1" class="space-y-6">
                    <div class="text-center">
                        <h3 class="text-lg font-medium text-gray-900 mb-2">Sélectionner un fichier JSON</h3>
                        <p class="text-gray-600 mb-6">Choisissez un fichier d'export de sections à importer</p>
                    </div>

                    <!-- Zone de drop -->
                    <div class="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors"
                        :class="{ 'border-blue-400 bg-blue-50': dragOver }" @dragover.prevent="dragOver = true"
                        @dragleave.prevent="dragOver = false" @drop.prevent="handleFileDrop">
                        <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12">
                            </path>
                        </svg>
                        <p class="text-lg font-medium text-gray-900 mb-2">
                            Glissez-déposez votre fichier ici
                        </p>
                        <p class="text-gray-600 mb-4">ou</p>
                        <label
                            class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors">
                            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                            </svg>
                            Parcourir les fichiers
                            <input type="file" accept=".json" class="hidden" @change="handleFileSelect" />
                        </label>
                    </div>

                    <!-- Fichier sélectionné -->
                    <div v-if="selectedFile" class="bg-gray-50 rounded-lg p-4">
                        <div class="flex items-center space-x-3">
                            <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                                </path>
                            </svg>
                            <div class="flex-1">
                                <p class="font-medium text-gray-900">{{ selectedFile.name }}</p>
                                <p class="text-sm text-gray-500">{{ formatFileSize(selectedFile.size) }}</p>
                            </div>
                            <button class="text-red-500 hover:text-red-700 transition-colors"
                                @click="selectedFile = null">
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16">
                                    </path>
                                </svg>
                            </button>
                        </div>
                    </div>

                    <!-- Erreur de parsing -->
                    <div v-if="importError" class="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div class="flex items-start space-x-3">
                            <svg class="w-5 h-5 text-red-500 mt-0.5" fill="none" stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                            <div>
                                <h4 class="font-medium text-red-800">Erreur de validation</h4>
                                <p class="text-red-700 mt-1">{{ importError }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Étape 2: Validation et aperçu -->
                <div v-if="step === 2" class="space-y-6">
                    <div class="text-center">
                        <h3 class="text-lg font-medium text-gray-900 mb-2">Validation du fichier</h3>
                        <p class="text-gray-600">Vérification des données et détection des conflits potentiels</p>
                    </div>

                    <!-- Indicateur de chargement -->
                    <div v-if="analyzing" class="text-center py-8">
                        <div class="inline-flex items-center space-x-3">
                            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <span class="text-gray-600">Analyse en cours...</span>
                        </div>
                    </div>

                    <!-- Résultats de validation -->
                    <div v-if="validation && !analyzing" class="space-y-4">
                        <!-- Statistiques -->
                        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div class="bg-blue-50 rounded-lg p-4 text-center">
                                <div class="text-2xl font-bold text-blue-600">{{ validation.sections_count }}</div>
                                <div class="text-sm text-blue-800">Sections</div>
                            </div>
                            <div class="bg-green-50 rounded-lg p-4 text-center">
                                <div class="text-2xl font-bold text-green-600">{{ validation.translations_count }}</div>
                                <div class="text-sm text-green-800">Traductions</div>
                            </div>
                            <div class="bg-purple-50 rounded-lg p-4 text-center">
                                <div class="text-2xl font-bold text-purple-600">{{ validation.field_configs_count }}
                                </div>
                                <div class="text-sm text-purple-800">Conditions</div>
                            </div>
                            <div class="bg-orange-50 rounded-lg p-4 text-center">
                                <div class="text-2xl font-bold text-orange-600">{{ validation.potential_conflicts }}
                                </div>
                                <div class="text-sm text-orange-800">Conflits</div>
                            </div>
                        </div>

                        <!-- Erreurs -->
                        <div v-if="validation.errors.length > 0" class="bg-red-50 border border-red-200 rounded-lg p-4">
                            <h4 class="font-medium text-red-800 mb-2">Erreurs détectées</h4>
                            <ul class="list-disc list-inside space-y-1">
                                <li v-for="error in validation.errors" :key="error" class="text-red-700">{{ error }}
                                </li>
                            </ul>
                        </div>

                        <!-- Avertissements -->
                        <div v-if="validation.warnings.length > 0"
                            class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <h4 class="font-medium text-yellow-800 mb-2">Avertissements</h4>
                            <ul class="list-disc list-inside space-y-1">
                                <li v-for="warning in validation.warnings" :key="warning" class="text-yellow-700">{{
                                    warning }}</li>
                            </ul>
                        </div>

                        <!-- Succès -->
                        <div v-if="validation.is_valid" class="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div class="flex items-center space-x-3">
                                <svg class="w-5 h-5 text-green-500" fill="none" stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M5 13l4 4L19 7"></path>
                                </svg>
                                <span class="font-medium text-green-800">Fichier valide et prêt pour l'analyse des
                                    conflits</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Étape 3: Résolution des conflits -->
                <div v-if="step === 3" class="space-y-6">
                    <div class="text-center">
                        <h3 class="text-lg font-medium text-gray-900 mb-2">Résolution des conflits</h3>
                        <p class="text-gray-600">{{ conflicts.length }} conflit(s) détecté(s) - Choisissez les actions à
                            effectuer</p>
                    </div>

                    <!-- Sections existantes (informatives) -->
                    <div v-if="existingSections.length > 0"
                        class="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                        <h4 class="font-medium text-gray-900 mb-2">Sections existantes (seront ignorées)</h4>
                        <div class="flex flex-wrap gap-2">
                            <span v-for="section in existingSections" :key="section"
                                class="inline-flex items-center px-2 py-1 bg-gray-200 text-gray-700 text-sm rounded">
                                {{ section }}
                            </span>
                        </div>
                    </div>

                    <!-- Conflits nécessitant une résolution -->
                    <div v-if="resolvableConflicts.length === 0" class="text-center py-8">
                        <svg class="mx-auto h-12 w-12 text-green-500 mb-4" fill="none" stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7">
                            </path>
                        </svg>
                        <h4 class="text-lg font-medium text-gray-900 mb-2">Aucun conflit à résoudre</h4>
                        <p class="text-gray-600">L'import peut être effectué sans intervention manuelle</p>
                    </div>

                    <!-- Liste des conflits -->
                    <div v-if="resolvableConflicts.length > 0" class="space-y-4">
                        <div v-for="conflict in resolvableConflicts" :key="conflict.id"
                            class="border border-gray-200 rounded-lg p-4">
                            <div class="flex items-start space-x-4">
                                <!-- Icône du type de conflit -->
                                <div class="flex-shrink-0">
                                    <div class="w-8 h-8 rounded-full flex items-center justify-center"
                                        :class="getConflictIconClass(conflict.type)">
                                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                :d="getConflictIconPath(conflict.type)"></path>
                                        </svg>
                                    </div>
                                </div>

                                <div class="flex-1 min-w-0">
                                    <!-- Description du conflit -->
                                    <div class="mb-3">
                                        <h4 class="font-medium text-gray-900">{{ getConflictTitle(conflict.type) }}</h4>
                                        <p class="text-sm text-gray-600 mt-1">{{ conflict.description }}</p>
                                        <div v-if="conflict.similarity_percentage" class="text-xs text-gray-500 mt-1">
                                            Similarité: {{ conflict.similarity_percentage.toFixed(1) }}%
                                        </div>
                                    </div>

                                    <!-- Valeurs en conflit -->
                                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        <div class="bg-red-50 rounded-lg p-3">
                                            <div class="text-xs font-medium text-red-800 mb-1">Valeur existante</div>
                                            <div class="text-sm text-red-700 font-mono">{{ conflict.existing_value ||
                                                '(vide)' }}</div>
                                        </div>
                                        <div class="bg-blue-50 rounded-lg p-3">
                                            <div class="text-xs font-medium text-blue-800 mb-1">Valeur importée</div>
                                            <div class="text-sm text-blue-700 font-mono">{{ conflict.imported_value ||
                                                '(vide)' }}</div>
                                        </div>
                                    </div>

                                    <!-- Actions de résolution -->
                                    <div class="space-y-2">
                                        <label class="text-sm font-medium text-gray-700">Action à effectuer:</label>
                                        <div class="space-y-2">
                                            <label v-for="action in getAvailableActions(conflict.type)"
                                                :key="action.value"
                                                class="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                                                <input type="radio" :name="`conflict_${conflict.id}`"
                                                    :value="action.value"
                                                    :checked="resolutions[conflict.id]?.resolution === action.value"
                                                    class="text-blue-600 focus:ring-blue-500"
                                                    @change="updateResolution(conflict.id, action.value)" />
                                                <div class="flex-1">
                                                    <div class="font-medium text-gray-900">{{ action.label }}</div>
                                                    <div class="text-sm text-gray-600">{{ action.description }}</div>
                                                </div>
                                            </label>
                                        </div>

                                        <!-- Valeur personnalisée -->
                                        <div v-if="resolutions[conflict.id]?.resolution === 'custom'" class="mt-3">
                                            <label class="block text-sm font-medium text-gray-700 mb-1">Valeur
                                                personnalisée:</label>
                                            <input type="text" :value="resolutions[conflict.id]?.custom_value || ''"
                                                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="Entrez une valeur personnalisée"
                                                @input="updateResolution(conflict.id, 'custom', ($event.target as HTMLInputElement).value)" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Étape 4: Résultats -->
                <div v-if="step === 4" class="space-y-6">
                    <div class="text-center">
                        <div v-if="importReport?.success" class="mb-4">
                            <svg class="mx-auto h-12 w-12 text-green-500 mb-4" fill="none" stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M5 13l4 4L19 7"></path>
                            </svg>
                            <h3 class="text-lg font-medium text-green-800 mb-2">Import réussi !</h3>
                        </div>
                        <div v-else class="mb-4">
                            <svg class="mx-auto h-12 w-12 text-red-500 mb-4" fill="none" stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                            <h3 class="text-lg font-medium text-red-800 mb-2">Erreur lors de l'import</h3>
                        </div>
                    </div>

                    <!-- Rapport d'import -->
                    <div v-if="importReport" class="space-y-4">
                        <!-- Statistiques -->
                        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div class="bg-green-50 rounded-lg p-4 text-center">
                                <div class="text-2xl font-bold text-green-600">{{ importReport.sections_created }}</div>
                                <div class="text-sm text-green-800">Créées</div>
                            </div>
                            <div class="bg-blue-50 rounded-lg p-4 text-center">
                                <div class="text-2xl font-bold text-blue-600">{{ importReport.sections_updated }}</div>
                                <div class="text-sm text-blue-800">Mises à jour</div>
                            </div>
                            <div class="bg-yellow-50 rounded-lg p-4 text-center">
                                <div class="text-2xl font-bold text-yellow-600">{{ importReport.sections_skipped }}
                                </div>
                                <div class="text-sm text-yellow-800">Ignorées</div>
                            </div>
                            <div class="bg-purple-50 rounded-lg p-4 text-center">
                                <div class="text-2xl font-bold text-purple-600">{{ importReport.hierarchy_repairs }}
                                </div>
                                <div class="text-sm text-purple-800">Réparations</div>
                            </div>
                        </div>

                        <!-- Détails -->
                        <div class="bg-gray-50 rounded-lg p-4">
                            <h4 class="font-medium text-gray-900 mb-3">Détails de l'import</h4>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                    <div class="flex justify-between py-1">
                                        <span class="text-gray-600">Traductions créées:</span>
                                        <span class="font-medium">{{ importReport.translations_created }}</span>
                                    </div>
                                    <div class="flex justify-between py-1">
                                        <span class="text-gray-600">Traductions mises à jour:</span>
                                        <span class="font-medium">{{ importReport.translations_updated }}</span>
                                    </div>
                                    <div class="flex justify-between py-1">
                                        <span class="text-gray-600">Conditions créées:</span>
                                        <span class="font-medium">{{ importReport.field_configs_created }}</span>
                                    </div>
                                </div>
                                <div>
                                    <div class="flex justify-between py-1">
                                        <span class="text-gray-600">Conditions mises à jour:</span>
                                        <span class="font-medium">{{ importReport.field_configs_updated }}</span>
                                    </div>
                                    <div class="flex justify-between py-1">
                                        <span class="text-gray-600">Sections orphelines corrigées:</span>
                                        <span class="font-medium">{{ importReport.orphaned_sections_fixed }}</span>
                                    </div>
                                    <div class="flex justify-between py-1">
                                        <span class="text-gray-600">Durée:</span>
                                        <span class="font-medium">{{ Math.round(importReport.duration_ms / 1000)
                                        }}s</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer -->
            <div class="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                <div class="flex items-center space-x-4">
                    <button v-if="step > 1 && step < 4"
                        class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        :disabled="importing || analyzing" @click="previousStep">
                        Précédent
                    </button>
                </div>

                <div class="flex items-center space-x-4">
                    <button
                        class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        :disabled="importing" @click="closeModal">
                        {{ step === 4 ? 'Fermer' : 'Annuler' }}
                    </button>

                    <button v-if="step === 1" :disabled="!selectedFile || analyzing"
                        class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        @click="parseFile">
                        <span v-if="analyzing" class="flex items-center space-x-2">
                            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Analyse...</span>
                        </span>
                        <span v-else>Analyser le fichier</span>
                    </button>

                    <button v-if="step === 2" :disabled="!validation?.is_valid || analyzing"
                        class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        @click="analyzeConflicts">
                        <span v-if="analyzing" class="flex items-center space-x-2">
                            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Analyse...</span>
                        </span>
                        <span v-else>Analyser les conflits</span>
                    </button>

                    <button v-if="step === 3" :disabled="importing"
                        class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        @click="executeImport">
                        <span v-if="importing" class="flex items-center space-x-2">
                            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Import en cours...</span>
                        </span>
                        <span v-else>Lancer l'import</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useSectionsImport } from '@/composables/useSectionsImport';
import { computed, ref, watch } from 'vue';

const props = defineProps<{
    isOpen: boolean;
}>();

const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'imported', report: any): void;
}>();

// Composable d'import
const {
    importing,
    analyzing,
    importError,
    step,
    selectedFile,
    validation,
    conflicts,
    resolutions,
    importReport,
    parseFile,
    analyzeConflicts,
    executeImport,
    reset,
    updateResolution
} = useSectionsImport();

// État local
const dragOver = ref(false);

// Computed properties pour l'affichage
const existingSections = computed((): string[] => {
    return conflicts.value
        .filter(c => c.type === 'existing_section')
        .map(c => c.section_name);
});

const resolvableConflicts = computed((): typeof conflicts.value => {
    return conflicts.value.filter(c => c.type !== 'existing_section');
});

/**
 * Formate la taille d'un fichier
 */
const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Gère la sélection de fichier
 */
const handleFileSelect = (event: Event): void => {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
        selectedFile.value = target.files[0];
    }
};

/**
 * Gère le drop de fichier
 */
const handleFileDrop = (event: DragEvent): void => {
    dragOver.value = false;
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
        selectedFile.value = event.dataTransfer.files[0];
    }
};

/**
 * Retourne à l'étape précédente
 */
const previousStep = (): void => {
    if (step.value > 1) {
        step.value--;
    }
};

/**
 * Ferme le modal
 */
const closeModal = (): void => {
    if (!importing.value) {
        reset();
        emit('close');
    }
};

/**
 * Retourne la classe CSS pour l'icône du conflit
 */
const getConflictIconClass = (type: string): string => {
    const classes = {
        existing_section: 'bg-gray-100 text-gray-600',
        similar_section: 'bg-yellow-100 text-yellow-600',
        translation_title: 'bg-blue-100 text-blue-600',
        translation_description: 'bg-blue-100 text-blue-600',
        invalid_condition: 'bg-red-100 text-red-600',
        condition_conflict: 'bg-orange-100 text-orange-600',
        missing_parent: 'bg-purple-100 text-purple-600',
        circular_dependency: 'bg-red-100 text-red-600'
    };
    return classes[type as keyof typeof classes] || 'bg-gray-100 text-gray-600';
};

/**
 * Retourne le path SVG pour l'icône du conflit
 */
const getConflictIconPath = (type: string): string => {
    const paths = {
        existing_section: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
        similar_section: 'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z',
        translation_title: 'M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129',
        translation_description: 'M4 6h16M4 12h16M4 18h7',
        invalid_condition: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
        condition_conflict: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
        missing_parent: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
        circular_dependency: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
    };
    return paths[type as keyof typeof paths] || 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
};

/**
 * Retourne le titre du conflit
 */
const getConflictTitle = (type: string): string => {
    const titles = {
        existing_section: 'Section existante',
        similar_section: 'Section similaire détectée',
        translation_title: 'Conflit de titre',
        translation_description: 'Conflit de description',
        invalid_condition: 'Condition invalide',
        condition_conflict: 'Conflit de condition',
        missing_parent: 'Section parente manquante',
        circular_dependency: 'Dépendance circulaire'
    };
    return titles[type as keyof typeof titles] || 'Conflit détecté';
};

/**
 * Retourne les actions disponibles pour un type de conflit
 */
const getAvailableActions = (type: string): Array<{ value: string; label: string; description: string }> => {
    const baseActions = {
        existing_section: [
            { value: 'skip', label: 'Ignorer (recommandé)', description: 'Cette section existe déjà et sera ignorée' }
        ],
        similar_section: [
            { value: 'create_new', label: 'Créer nouvelle section', description: 'Créer une nouvelle section avec le nom importé' },
            { value: 'align_existing', label: 'Aligner avec existante', description: 'Utiliser la section existante similaire' },
            { value: 'skip', label: 'Ignorer', description: 'Ne pas importer cette section' }
        ],
        translation_title: [
            { value: 'keep_existing', label: 'Conserver existant', description: 'Garder le titre actuel' },
            { value: 'use_imported', label: 'Utiliser importé', description: 'Remplacer par le titre importé' },
            { value: 'custom', label: 'Valeur personnalisée', description: 'Définir un titre personnalisé' }
        ],
        translation_description: [
            { value: 'keep_existing', label: 'Conserver existante', description: 'Garder la description actuelle' },
            { value: 'use_imported', label: 'Utiliser importée', description: 'Remplacer par la description importée' },
            { value: 'merge', label: 'Fusionner', description: 'Combiner les deux descriptions' },
            { value: 'custom', label: 'Valeur personnalisée', description: 'Définir une description personnalisée' }
        ],
        invalid_condition: [
            { value: 'create_new', label: 'Créer section manquante', description: 'Créer automatiquement la section de condition' },
            { value: 'skip', label: 'Ignorer condition', description: 'Importer sans la condition' }
        ],
        condition_conflict: [
            { value: 'keep_existing', label: 'Conserver existante', description: 'Garder la condition actuelle' },
            { value: 'use_imported', label: 'Utiliser importée', description: 'Remplacer par la condition importée' },
            { value: 'skip', label: 'Ignorer', description: 'Ne pas modifier la condition' }
        ]
    };

    return baseActions[type as keyof typeof baseActions] || [
        { value: 'skip', label: 'Ignorer', description: 'Ignorer ce conflit' }
    ];
};

// Watcher pour émettre l'événement d'import terminé
watch(() => importReport.value, (report) => {
    if (report && report.success) {
        emit('imported', report);
    }
});

// Réinitialiser quand le modal se ferme
watch(() => props.isOpen, (isOpen) => {
    if (!isOpen) {
        reset();
    }
});
</script>